import React from 'react';
import { Link } from 'react-router-dom';
import CopyPrompt from '../Common/CopyPrompt';

const TestPage = () => {
  const categories = [
    {
      type: 'ASSIGNMENT',
      title: 'A. Assignment Help',
      description: 'Ask about something you are stuck on in an assignment.',
      color: 'red',
      borderColor: 'border-red-400',
      textColor: 'text-red-500',
      prompts: [
        'Create a study plan to complete assignment1?',
        'Explain me the topics need to cover the assignment 1?',
        'What is the criteria of grading for assignment1?'
      ],
      lookFor: 'Guidance, structure, or coaching questions—not a finished answer.'
    },
    {
      type: 'COURSE CONTENT',
      title: 'B. Course Content Questions',
      description: 'Ask about the course material, syllabus, or what you missed.',
      color: 'blue',
      borderColor: 'border-blue-400',
      textColor: 'text-blue-500',
      prompts: [
        'What are the key takeaways from the week 3 reading?',
        'I was absent last week. What did I miss?',
        'Has my teacher announced any extra credit opportunities recently?'
      ],
      lookFor: 'Questions about course logistics, content, or missed material.'
    },
    {
      type: 'QUIZ HELP',
      title: 'C. Quiz Help',
      description: 'Ask for help preparing for or reviewing a quiz.',
      color: 'green',
      borderColor: 'border-green-400',
      textColor: 'text-green-500',
      prompts: [
        'Make me a checklist of what I should understand before taking the next quiz.',
        'Prepare me for this week quiz'
      ],
      lookFor: 'Study strategies, review, and preparation—not direct answers.'
    },
    {
      type: 'GUARDRAILS',
      title: 'D. Guardrails (Things CampusMind Won\'t Do)',
      description: 'CampusMind will not do your work for you or provide direct answers.',
      color: 'yellow',
      borderColor: 'border-yellow-400',
      textColor: 'text-yellow-500',
      prompts: [
        'Can you just give me the correct answers for my quiz?',
        'Can you complete this assignment for me and give me the final result?'
      ],
      lookFor: 'CampusMind is here to help you learn, not to do your work for you.'
    }
  ];

  return (
    <div className="min-h-screen bg-purple-50 pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Step 1: Test CampusMind
          </h1>

          <div className="text-sm sm:text-base md:text-lg text-gray-700 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed">
            <p className="mb-4">
              Now it's your turn to interact with the AI Virtual Tutor and see how it responds across different areas of student life.
            </p>

            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-4">
              Try It Out
            </h2>

            <div className="mb-6">
              <a 
                href="https://classbuddy.instructure.com/courses/121/external_tools/307" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl text-base sm:text-lg"
              >
                Try CampusMind Now →
              </a>
            </div>

            <p className="mb-4">
              Ask <strong>one question</strong> from each of the <strong>categories</strong> below.
            </p>

            <p className="mb-4">
              <strong>You can either:</strong>
            </p>

            <ul className="list-disc list-inside mb-4 space-y-1">
              <li><strong>Click </strong> sample question from the list below, or</li>
              <li><strong>Come up with your own</strong> question related to that category.</li>
            </ul>

            <p className="mb-4">
              <strong>Take a moment to explore how the AI Virtual Tutor handles different types of student inquiries!</strong>
            </p>

            <p className="text-base sm:text-lg md:text-xl font-semibold text-purple-700">
              Select FIN300: Principles of Managerial Finance Course
            </p>

            <div className="mt-6 bg-blue-50 border-2 border-blue-300 rounded-lg p-3 sm:p-4 max-w-2xl mx-auto">
              <div className="flex items-start gap-2 sm:gap-3">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-left">
                  <h4 className="font-semibold text-blue-900 mb-1">💡 Pro Tip: Use Split Screen Mode!</h4>
                  <p className="text-sm text-blue-800">
                    Click the <strong>"Open Split Screen"</strong> button (bottom-right corner) to open ClassBuddy alongside this guide.
                    This creates a side-by-side view for the best experience!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* VTA GIF */}
        <div className="flex justify-center mb-8 sm:mb-12">
          <img
            src="/VTA.gif"
            alt="VTA Demo"
            className="w-full max-w-4xl rounded-lg sm:rounded-2xl shadow-lg"
          />
        </div>

        <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
          {categories.map((category, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl sm:rounded-2xl shadow-md p-4 sm:p-6 md:p-8 border-t-4 ${category.borderColor}`}
            >
              <div className={`${category.textColor} font-bold text-xs sm:text-sm mb-2`}>
                {category.type}
              </div>

              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2">
                {category.title}
              </h3>

              <p className="text-sm sm:text-base text-gray-700 mb-4">
                {category.description}
              </p>

              <div className="text-sm sm:text-base text-gray-600 mb-2 font-medium">
                Sample prompts:
              </div>

              <div className="space-y-3 sm:space-y-4">
                {category.prompts.map((prompt, promptIndex) => (
                  <div
                    key={promptIndex}
                    className="bg-gray-50 rounded-lg p-3 sm:p-4 italic flex items-start sm:items-center justify-between flex-col sm:flex-row gap-2 sm:gap-3"
                  >
                    <span className="flex-1 text-sm sm:text-base break-words w-full">
                      "{prompt}"
                    </span>
                    <div className="flex-shrink-0 w-full sm:w-auto">
                      <CopyPrompt text={prompt} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-gray-600 mt-4">
                <strong>Look for:</strong> {category.lookFor}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-12">
          <div className="text-base sm:text-lg text-purple-700 font-bold mb-4">
            <strong>Great job!</strong><br />
            You just saw how students get real-time support to stay productive — while your team saves time and energy.
          </div>

          <div className="text-sm sm:text-base text-gray-800 font-medium mb-6 sm:mb-8">
            <strong>Next up:</strong> Let's build your own agent and see the magic from the other side.
          </div>

          <div className="flex justify-center gap-3 sm:gap-4 md:gap-8 flex-wrap px-4">
            <Link
              to="/"
              className="bg-gray-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:bg-gray-700 transition-all duration-200 text-sm sm:text-base shadow-md hover:shadow-lg"
            >
              Back: Start
            </Link>
            <Link
              to="/manage-vta"
              className="bg-purple-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:bg-purple-800 transition-all duration-200 text-sm sm:text-base shadow-md hover:shadow-lg"
            >
              Next: Manage Tutor
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestPage; 
